import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Save, X, Edit3, Plus, Trash2, Download, Upload, AlertCircle, Check, RefreshCw, Edit } from "lucide-react";
import api from "../api/client";

// Financial year options
const fyOptions = ["FY 2024", "FY 2025", "FY 2026"];

// Budget type options
const budgetTypes = [
  { value: "quantity", label: "Budget Quantity" },
  { value: "amount", label: "Budget Amount" }
];

// Month columns
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Generate unique ID
const generateId = () => `budget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper function to convert FY + Month to PostgreSQL date format
const convertToDate = (fiscalYear, month) => {
  const fyYear = parseInt(fiscalYear.split(" ")[1]);
  const monthIndex = months.indexOf(month);
  const year = fyYear;
  const monthNum = String(monthIndex + 1).padStart(2, '0');
  return `${year}-${monthNum}-01`; // Added -01 for the day
};

// Helper function to get month name from date string
const getMonthFromDate = (dateString) => {
  const date = new Date(dateString);
  const monthNum = date.getMonth();
  return months[monthNum];
};

// Helper function to get FY from date string
const getFYFromDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  return `FY ${year}`;
};

// Toast Notification Component
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
  const Icon = type === 'success' ? Check : type === 'error' ? X : AlertCircle;

  return (
    <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-4 rounded-lg shadow-2xl flex items-center space-x-3 z-50 animate-slide-in`}>
      <Icon size={24} />
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-4 hover:bg-white/20 rounded p-1">
        <X size={18} />
      </button>
    </div>
  );
}

// Modal Component for Editing Row
function EditModal({ budget, salesReps, items, budgetType, onSave, onClose }) {
  const [editedBudget, setEditedBudget] = useState({ ...budget });
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedBudget);
      // Success handled by parent component
    } catch (error) {
      // Error handled by parent component
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            Edit Budget Line - {budgetType === 'quantity' ? 'Quantity' : 'Amount'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fiscal Year</label>
              <select
                value={editedBudget.fiscalYear}
                onChange={(e) => setEditedBudget({ ...editedBudget, fiscalYear: e.target.value })}
                disabled={isSaving}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              >
                {fyOptions.map(fy => (
                  <option key={fy} value={fy}>{fy}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sales Rep</label>
              <select
                value={editedBudget.salesRepId}
                onChange={(e) => {
                  const rep = salesReps.find(r => r.sales_rep_id === e.target.value);
                  setEditedBudget({
                    ...editedBudget,
                    salesRepId: e.target.value,
                    salesRepName: rep?.sales_rep || ""
                  });
                }}
                disabled={isSaving}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              >
                {salesReps.map(rep => (
                  <option key={rep.sales_rep_id} value={rep.sales_rep_id}>
                    {rep.sales_rep}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Item</label>
              <select
                value={editedBudget.itemId}
                onChange={(e) => {
                  const item = items.find(i => i.item_id === e.target.value);
                  setEditedBudget({
                    ...editedBudget,
                    itemId: e.target.value,
                    itemName: item?.item_name || ""
                  });
                }}
                disabled={isSaving}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              >
                {items.map(item => (
                  <option key={item.item_id} value={item.item_id}>
                    {item.item_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Monthly Values */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Monthly Budget Values ({budgetType === 'quantity' ? 'Quantity' : 'Amount ($)'})
            </label>
            <div className="grid grid-cols-4 gap-3">
              {months.map(month => (
                <div key={month}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{month}</label>
                  <input
                    type="number"
                    step={budgetType === 'amount' ? '0.01' : '1'}
                    value={editedBudget.monthlyData?.[month]?.value || ""}
                    onChange={(e) => setEditedBudget({ 
                      ...editedBudget, 
                      monthlyData: {
                        ...editedBudget.monthlyData,
                        [month]: {
                          ...editedBudget.monthlyData?.[month],
                          value: e.target.value
                        }
                      }
                    })}
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-right disabled:opacity-50"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium shadow-lg disabled:opacity-50 flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Page4() {
  // Main data state
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [selectedFY, setSelectedFY] = useState("FY 2024");
  const [selectedBudgetType, setSelectedBudgetType] = useState("quantity");
  const [selectedRep, setSelectedRep] = useState("ALL");
  const [searchItem, setSearchItem] = useState("");

  // Data from backend
  const [salesReps, setSalesReps] = useState([]);
  const [items, setItems] = useState([]);
  const [rawQuantityData, setRawQuantityData] = useState([]);
  const [rawAmountData, setRawAmountData] = useState([]);

  // Modal state
  const [editingBudget, setEditingBudget] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Ref to prevent double-fetching
  const isFetching = useRef(false);

  // Show toast notification
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  // Transform budget data from API format to display format
  const transformBudgetData = useCallback((budgetData, budgetType) => {
    const groupedData = {};

    budgetData.forEach(record => {
      const fy = getFYFromDate(record.month);
      const key = `${fy}-${record.sales_rep_id}-${record.item_id}`;

      if (!groupedData[key]) {
        groupedData[key] = {
          id: generateId(),
          fiscalYear: fy,
          salesRepId: record.sales_rep_id,
          salesRepName: record.sales_rep,
          itemId: record.item_id,
          itemName: record.item_name,
          monthlyData: {}
        };
      }

      const monthName = getMonthFromDate(record.month);
      const value = budgetType === 'quantity' 
        ? record.budgeted_quantity 
        : record.budgeted_amount;
      
      groupedData[key].monthlyData[monthName] = {
        value: value || "",
        dbId: record.id,
        originalValue: value || ""
      };
    });

    return Object.values(groupedData);
  }, []);

  // Fetch all data function
  const fetchAllData = useCallback(async () => {
    if (isFetching.current) {
      console.log('Already fetching, skipping...');
      return;
    }

    isFetching.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching data from API...');

      const [repsResponse, itemsResponse, qtyResponse, amtResponse] = await Promise.all([
        api.get('/api/salesreps'),
        api.get('/api/items'),
        api.get('/api/budget/quantities'),
        api.get('/api/budget/amounts')
      ]);

      console.log('Raw responses received');

      const repsData = repsResponse.data.rows || [];
      const itemsData = itemsResponse.data.rows || [];
      const quantityData = qtyResponse.data.rows || [];
      const amountData = amtResponse.data.rows || [];

      console.log('Extracted data counts:', {
        reps: repsData.length,
        items: itemsData.length,
        quantities: quantityData.length,
        amounts: amountData.length
      });

      const transformedReps = repsData.map(rep => ({
        sales_rep_id: rep["Employee ID"],
        sales_rep: rep["Employee Name"]
      }));

      const transformedItems = itemsData.map(item => ({
        item_id: item["Item ID"],
        item_name: item["Item Name"]
      }));

      console.log('Data transformed successfully');

      setSalesReps(transformedReps);
      setItems(transformedItems);
      setRawQuantityData(quantityData);
      setRawAmountData(amountData);

      // Transform and set initial budgets based on selected type
      const transformedBudgets = transformBudgetData(quantityData, 'quantity');
      setBudgets(transformedBudgets);

      setLoading(false);
      console.log('Data load complete');
    } catch (err) {
      console.error('Error fetching data:', err);
      console.error('Error details:', err.response?.data);
      setError(`Failed to load budget data: ${err.message}`);
      setLoading(false);
      showToast('Failed to load budget data', 'error');
    } finally {
      isFetching.current = false;
    }
  }, [transformBudgetData, showToast]);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Update budgets when budget type changes
  useEffect(() => {
    if (rawQuantityData.length > 0 || rawAmountData.length > 0) {
      const dataToUse = selectedBudgetType === 'quantity' ? rawQuantityData : rawAmountData;
      const transformedBudgets = transformBudgetData(dataToUse, selectedBudgetType);
      setBudgets(transformedBudgets);
    }
  }, [selectedBudgetType, rawQuantityData, rawAmountData, transformBudgetData]);

  // Filter budgets based on selection
  const filteredBudgets = useMemo(() => {
    const filtered = budgets.filter(budget => {
      const fyMatch = budget.fiscalYear === selectedFY;
      const repMatch = selectedRep === "ALL" || budget.salesRepId === selectedRep;
      const itemMatch = searchItem === "" ||
        budget.itemName.toLowerCase().includes(searchItem.toLowerCase()) ||
        budget.itemId.toLowerCase().includes(searchItem.toLowerCase());
      return fyMatch && repMatch && itemMatch;
    });

    console.log('Filtered budgets:', filtered.length);
    return filtered;
  }, [budgets, selectedFY, selectedRep, searchItem]);

  // Calculate totals
  const totals = useMemo(() => {
    const monthTotals = {};
    months.forEach(month => {
      monthTotals[month] = filteredBudgets.reduce((sum, budget) => {
        const val = parseFloat(budget.monthlyData?.[month]?.value) || 0;
        return sum + val;
      }, 0);
    });

    const grandTotal = Object.values(monthTotals).reduce((sum, val) => sum + val, 0);

    return { ...monthTotals, grandTotal };
  }, [filteredBudgets]);

  // Add new budget row
  const handleAddRow = useCallback(() => {
    const firstRep = salesReps[0];
    const firstItem = items[0];

    const newId = generateId();
    const newBudget = {
      id: newId,
      fiscalYear: selectedFY,
      salesRepId: firstRep?.sales_rep_id || "",
      salesRepName: firstRep?.sales_rep || "",
      itemId: firstItem?.item_id || "",
      itemName: firstItem?.item_name || "",
      monthlyData: {}
    };

    // Initialize empty monthly data
    months.forEach(month => {
      newBudget.monthlyData[month] = {
        value: "",
        dbId: null,
        originalValue: ""
      };
    });

    // Open modal for the new budget
    setEditingBudget(newBudget);
  }, [selectedFY, salesReps, items]);

  // Delete row
  const handleDeleteRow = useCallback(async (id) => {
    if (window.confirm('Are you sure you want to delete this budget line? This will delete all monthly data for this combination.')) {
      const budgetToDelete = budgets.find(b => b.id === id);
      
      // Collect all dbIds to delete
      const idsToDelete = [];
      months.forEach(month => {
        const dbId = budgetToDelete?.monthlyData?.[month]?.dbId;
        if (dbId) {
          idsToDelete.push(dbId);
        }
      });

      if (idsToDelete.length === 0) {
        // No database records to delete, just remove from state
        setBudgets(prev => prev.filter(budget => budget.id !== id));
        showToast('Budget line removed', 'success');
        return;
      }

      try {
        const endpoint = selectedBudgetType === 'quantity' 
          ? '/api/budgets/quantities/save' 
          : '/api/budgets/amounts/save';

        await api.post(endpoint, {
          records: [],
          deletedRecords: idsToDelete
        });

        setBudgets(prev => prev.filter(budget => budget.id !== id));
        showToast('Budget line deleted successfully', 'success');
        
        // Refresh data to get updated list
        await fetchAllData();
      } catch (error) {
        console.error('Delete error:', error);
        showToast('Failed to delete budget line', 'error');
      }
    }
  }, [budgets, selectedBudgetType, showToast, fetchAllData]);

  // Handle editing a budget
  const handleEditBudget = useCallback((budget) => {
    setEditingBudget(budget);
  }, []);

  // Transform React state back to PostgreSQL format for saving
  const transformToPostgresFormat = useCallback((budgetRecord) => {
    const records = [];

    months.forEach(month => {
      const monthData = budgetRecord.monthlyData?.[month];
      const value = monthData?.value;
      
      if (value !== "" && value !== null && value !== undefined) {
        const monthDate = convertToDate(budgetRecord.fiscalYear, month);

        const record = {
          id: monthData?.dbId || null,
          month: monthDate,
          sales_rep: budgetRecord.salesRepName,
          sales_rep_id: budgetRecord.salesRepId,
          item_name: budgetRecord.itemName,
          item_id: budgetRecord.itemId
        };

        if (selectedBudgetType === 'quantity') {
          record.budgeted_quantity = parseFloat(value);
        } else {
          record.budgeted_amount = parseFloat(value);
        }

        records.push(record);
      }
    });

    return records;
  }, [selectedBudgetType]);

  // Save edited budget (called from modal)
  const handleSaveBudget = useCallback(async (editedBudget) => {
    try {
      const records = transformToPostgresFormat(editedBudget);
      
      const endpoint = selectedBudgetType === 'quantity' 
        ? '/api/budgets/quantities/save' 
        : '/api/budgets/amounts/save';

      await api.post(endpoint, {
        records,
        deletedRecords: []
      });

      // Update local state
      setBudgets(prev => {
        const existingIndex = prev.findIndex(b => b.id === editedBudget.id);
        if (existingIndex >= 0) {
          // Update existing
          const updated = [...prev];
          updated[existingIndex] = editedBudget;
          return updated;
        } else {
          // Add new
          return [...prev, editedBudget];
        }
      });

      showToast('Budget saved successfully', 'success');
      setEditingBudget(null);
      
      // Refresh data to get updated dbIds
      await fetchAllData();
    } catch (error) {
      console.error('Save error:', error);
      showToast('Failed to save budget', 'error');
      throw error; // Re-throw to let modal handle it
    }
  }, [selectedBudgetType, transformToPostgresFormat, showToast, fetchAllData]);

  // Export to CSV
  const handleExportCSV = useCallback(() => {
    const typeLabel = selectedBudgetType === 'quantity' ? 'Quantity' : 'Amount';
    const headers = ["Fiscal Year", "Sales Rep", "Sales Rep ID", "Item Name", "Item ID", ...months, "Total"];

    const rows = filteredBudgets.map(budget => {
      const rowTotal = months.reduce((sum, month) => {
        return sum + (parseFloat(budget.monthlyData?.[month]?.value) || 0);
      }, 0);
      
      return [
        budget.fiscalYear,
        budget.salesRepName,
        budget.salesRepId,
        budget.itemName,
        budget.itemId,
        ...months.map(month => budget.monthlyData?.[month]?.value || 0),
        rowTotal
      ];
    });

    rows.push([
      "TOTAL", "", "", "", "",
      ...months.map(month => totals[month]),
      totals.grandTotal
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `budget-${typeLabel}-${selectedFY.replace(' ', '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    showToast('CSV exported successfully', 'success');
  }, [filteredBudgets, selectedFY, selectedBudgetType, totals, showToast]);

  // Calculate row total
  const getRowTotal = useCallback((budget) => {
    return months.reduce((sum, month) => {
      return sum + (parseFloat(budget.monthlyData?.[month]?.value) || 0);
    }, 0);
  }, []);

  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    fetchAllData();
    showToast('Data refreshed', 'success');
  }, [fetchAllData, showToast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading budget data...</p>
          <p className="text-gray-500 text-sm mt-2">Processing large dataset, please wait...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Error Loading Data</h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="w-full flex items-center justify-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <RefreshCw size={18} />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto px-6 py-8">
        {/* Toast Notifications */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Budget Management</h1>
              <p className="text-gray-600 text-lg">
                Create and manage sales budgets - {selectedBudgetType === 'quantity' ? 'Quantity' : 'Amount ($)'}
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className="text-sm text-gray-500">
                  Total {selectedBudgetType === 'quantity' ? 'Quantity' : 'Amount'} ({selectedFY})
                </p>
                <p className="text-2xl font-bold text-indigo-600">
                  {selectedBudgetType === 'quantity' 
                    ? totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 0 })
                    : `$${totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Filters */}
              <div className="flex items-center space-x-4 flex-wrap">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Fiscal Year</label>
                  <select
                    value={selectedFY}
                    onChange={(e) => setSelectedFY(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white min-w-32"
                  >
                    {fyOptions.map(fy => (
                      <option key={fy} value={fy}>{fy}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Budget Type</label>
                  <select
                    value={selectedBudgetType}
                    onChange={(e) => setSelectedBudgetType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white min-w-44"
                  >
                    {budgetTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Sales Rep</label>
                  <select
                    value={selectedRep}
                    onChange={(e) => setSelectedRep(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white min-w-48"
                  >
                    <option value="ALL">All Sales Reps</option>
                    {salesReps.map(rep => (
                      <option key={rep.sales_rep_id} value={rep.sales_rep_id}>
                        {rep.sales_rep}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item Search Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Search Item</label>
                  <input
                    type="text"
                    value={searchItem}
                    onChange={(e) => setSearchItem(e.target.value)}
                    placeholder="Filter by item name or ID..."
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white min-w-64"
                  />
                </div>

                {/* Data info */}
                <div className="text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                  <p>{salesReps.length} reps loaded</p>
                  <p>{items.length} items loaded</p>
                  <p>{budgets.length} total budget lines</p>
                  <p className="font-semibold text-indigo-600">{filteredBudgets.length} showing</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className={`flex items-center space-x-2 font-semibold px-5 py-2.5 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 ${loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white hover:shadow-xl'
                    }`}
                >
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                  <span>{loading ? 'Loading...' : 'Refresh'}</span>
                </button>

                <button
                  onClick={handleAddRow}
                  disabled={salesReps.length === 0 || items.length === 0}
                  className={`flex items-center space-x-2 font-semibold px-5 py-2.5 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 ${salesReps.length > 0 && items.length > 0
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white hover:shadow-xl'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  title={`Add new ${selectedBudgetType === 'quantity' ? 'Quantity' : 'Amount'} budget line`}
                >
                  <Plus size={18} />
                  <span>Add {selectedBudgetType === 'quantity' ? 'Quantity' : 'Amount'} Line</span>
                </button>

                <button
                  onClick={handleExportCSV}
                  disabled={filteredBudgets.length === 0}
                  className={`flex items-center space-x-2 font-semibold px-5 py-2.5 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 ${filteredBudgets.length > 0
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white hover:shadow-xl'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  <Download size={18} />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Table - READ ONLY with Edit Button */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: '70vh' }}>
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-4 text-left font-semibold w-24">Actions</th>
                  <th className="px-3 py-4 text-left font-semibold min-w-28">FY</th>
                  <th className="px-3 py-4 text-left font-semibold min-w-44">Sales Rep</th>
                  <th className="px-3 py-4 text-left font-semibold min-w-64">Item</th>
                  {months.map(month => (
                    <th key={month} className="px-3 py-4 text-right font-semibold min-w-24">{month}</th>
                  ))}
                  <th className="px-3 py-4 text-right font-semibold min-w-28 bg-indigo-700">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBudgets.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center space-y-3">
                        <AlertCircle size={48} className="text-gray-300" />
                        <p className="text-lg font-medium">No budget lines found</p>
                        <p className="text-sm">
                          {searchItem
                            ? `No items match "${searchItem}". Try a different search.`
                            : `Click "Add ${selectedBudgetType === 'quantity' ? 'Quantity' : 'Amount'} Line" to create one for ${selectedFY}`
                          }
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBudgets.map((budget, rowIndex) => (
                    <tr key={budget.id} className={`hover:bg-gray-50 transition-colors duration-150 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                      {/* Actions */}
                      <td className="px-3 py-2">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleEditBudget(budget)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                            title="Edit Row"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteRow(budget.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                            title="Delete Row"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>

                      {/* Display Only - No dropdowns */}
                      <td className="px-3 py-2 text-gray-900">{budget.fiscalYear}</td>
                      <td className="px-3 py-2 text-gray-900">{budget.salesRepName}</td>
                      <td className="px-3 py-2 text-gray-900 truncate" title={budget.itemName}>
                        {budget.itemName}
                      </td>

                      {/* Month Values - Display Only */}
                      {months.map(month => {
                        const value = budget.monthlyData?.[month]?.value;
                        return (
                          <td key={month} className="px-3 py-2 text-right font-mono">
                            <span className={value ? 'text-gray-900' : 'text-gray-300'}>
                              {value !== "" && value !== null && value !== undefined
                                ? parseFloat(value).toLocaleString(undefined, {
                                    minimumFractionDigits: selectedBudgetType === 'amount' ? 2 : 0,
                                    maximumFractionDigits: selectedBudgetType === 'amount' ? 2 : 0
                                  })
                                : '-'
                              }
                            </span>
                          </td>
                        );
                      })}

                      {/* Row Total */}
                      <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700 bg-indigo-50">
                        {selectedBudgetType === 'quantity' 
                          ? getRowTotal(budget).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                          : `$${getRowTotal(budget).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                        }
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {/* Totals Footer */}
              {filteredBudgets.length > 0 && (
                <tfoot className="bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-300 sticky bottom-0">
                  <tr className="font-bold">
                    <td colSpan={4} className="px-3 py-4 text-right text-gray-700">TOTALS</td>
                    {months.map(month => (
                      <td key={month} className="px-3 py-4 text-right font-mono text-gray-800">
                        {selectedBudgetType === 'quantity'
                          ? totals[month].toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                          : `$${totals[month].toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                        }
                      </td>
                    ))}
                    <td className="px-3 py-4 text-right font-mono text-indigo-700 bg-indigo-100">
                      {selectedBudgetType === 'quantity'
                        ? totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                        : `$${totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                      }
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>Click the "Edit" button to modify a budget line • Changes are saved immediately to the database</p>
          <p className="mt-1">Tip: Use the "Search Item" filter above to quickly find specific items</p>
        </div>
      </div>

      {/* Edit Modal */}
      {editingBudget && (
        <EditModal
          budget={editingBudget}
          salesReps={salesReps}
          items={items}
          budgetType={selectedBudgetType}
          onSave={handleSaveBudget}
          onClose={() => setEditingBudget(null)}
        />
      )}
    </div>
  );
}

export default Page4;